import React, { useEffect, useState } from 'react';
import { CheckCircle, KeyRound, RefreshCw, Save, Server, SlidersHorizontal, Zap } from 'lucide-react';
import { getAiIntegrationSettings, getSystemHealth, saveAiIntegrationSettings, testAiIntegration } from '../api/services';
import './Settings.css';

type AiProvider = 'openai' | 'groq' | 'custom';
type Theme = 'dark' | 'light';

export default function Settings() {
  const [theme, setTheme] = useState<Theme>(() => localStorage.getItem('settings-theme') === 'light' ? 'light' : 'dark');
  const [autoRefresh, setAutoRefresh] = useState(() => localStorage.getItem('settings-auto-refresh') !== 'false');
  const [compactTables, setCompactTables] = useState(() => localStorage.getItem('settings-compact-tables') === 'true');
  const [health, setHealth] = useState<{ status: string; timestamp?: string } | null>(null);
  const [error, setError] = useState('');
  const [aiSettings, setAiSettings] = useState<{ provider: AiProvider; baseUrl: string; model: string; apiKey: string }>({ provider: 'openai', baseUrl: '', model: '', apiKey: '' });
  const [aiStatus, setAiStatus] = useState({ keyConfigured: false, keySuffix: null as string | null });
  const [aiSaving, setAiSaving] = useState(false);
  const [aiTesting, setAiTesting] = useState(false);
  const [aiMessage, setAiMessage] = useState('');

  const checkHealth = async () => {
    setError('');
    try { setHealth(await getSystemHealth()); } catch { setHealth(null); setError('The API health check failed.'); }
  };

  useEffect(() => { void checkHealth(); }, []);
  useEffect(() => {
    void getAiIntegrationSettings().then((settings) => {
      setAiSettings({ provider: settings.provider, baseUrl: settings.baseUrl, model: settings.model, apiKey: '' });
      setAiStatus({ keyConfigured: settings.keyConfigured, keySuffix: settings.keySuffix });
    }).catch(() => setError('Unable to load AI integration settings.'));
  }, []);

  const updatePreference = (key: string, value: boolean, setter: (next: boolean) => void) => {
    setter(value);
    localStorage.setItem(key, String(value));
  };

  const updateTheme = (nextTheme: Theme) => {
    setTheme(nextTheme);
    localStorage.setItem('settings-theme', nextTheme);
    document.documentElement.dataset.theme = nextTheme;
  };

  const chooseProvider = (provider: AiProvider) => {
    setAiSettings((current) => ({
      ...current,
      provider,
      baseUrl: provider === 'groq' ? 'https://api.groq.com/openai/v1' : provider === 'openai' ? 'https://api.openai.com/v1' : current.baseUrl,
      model: provider === 'groq' ? 'openai/gpt-oss-20b' : provider === 'openai' ? 'gpt-4o-mini' : current.model,
    }));
  };

  const saveAiSettings = async () => {
    setAiSaving(true); setError(''); setAiMessage('');
    try {
      const saved = await saveAiIntegrationSettings(aiSettings);
      setAiSettings((current) => ({ ...current, apiKey: '' }));
      setAiStatus({ keyConfigured: saved.keyConfigured, keySuffix: saved.keySuffix });
      setAiMessage('AI integration settings saved.');
    } catch { setError('Unable to save AI integration settings.'); } finally { setAiSaving(false); }
  };

  const testAiSettings = async () => {
    setAiTesting(true); setError(''); setAiMessage('');
    try { await testAiIntegration(); setAiMessage('AI connection succeeded.'); } catch { setError('AI connection failed. Check the provider URL, model, and API key.'); } finally { setAiTesting(false); }
  };

  return (
    <div className="settings-page">
      <div className="page-header"><h2>Settings</h2><p>Admin panel preferences and service status.</p></div>
      {error && <div className="error">{error}</div>}
      {aiMessage && <div className="settings-message">{aiMessage}</div>}
      <div className="settings-grid">
        <section className="card settings-card">
          <div className="settings-title"><SlidersHorizontal size={19} /><div><h3>Interface preferences</h3><p>These preferences are stored in this browser.</p></div></div>
          <label className="setting-row"><span><strong>Colour theme</strong><small>Choose the appearance for this admin panel.</small></span><select className="theme-select" value={theme} onChange={(event) => updateTheme(event.target.value as Theme)}><option value="dark">Dark</option><option value="light">Light</option></select></label>
          <label className="setting-row"><span><strong>Automatic data refresh</strong><small>Refresh data when returning to a page.</small></span><input type="checkbox" checked={autoRefresh} onChange={(event) => updatePreference('settings-auto-refresh', event.target.checked, setAutoRefresh)} /></label>
          <label className="setting-row"><span><strong>Compact tables</strong><small>Use tighter rows for high-volume lists.</small></span><input type="checkbox" checked={compactTables} onChange={(event) => updatePreference('settings-compact-tables', event.target.checked, setCompactTables)} /></label>
        </section>
        <section className="card settings-card ai-settings-card">
          <div className="settings-title"><KeyRound size={19} /><div><h3>Tutorial AI integration</h3><p>Used to read PDFs and generate desktop tutorial steps.</p></div></div>
          <div className="ai-settings-fields">
            <label>AI provider<select value={aiSettings.provider} onChange={(event) => chooseProvider(event.target.value as AiProvider)}><option value="openai">OpenAI</option><option value="groq">Groq</option><option value="custom">Custom OpenAI-compatible</option></select></label>
            <label>Provider URL<input value={aiSettings.baseUrl} onChange={(event) => setAiSettings({ ...aiSettings, baseUrl: event.target.value })} placeholder="https://api.openai.com/v1" /></label>
            <label>Model<input value={aiSettings.model} onChange={(event) => setAiSettings({ ...aiSettings, model: event.target.value })} placeholder="gpt-4o-mini" /></label>
            <label>API key<input type="password" value={aiSettings.apiKey} onChange={(event) => setAiSettings({ ...aiSettings, apiKey: event.target.value })} placeholder={aiStatus.keyConfigured ? `Configured ending ${aiStatus.keySuffix}` : 'Enter API key'} /></label>
          </div>
          <div className="ai-settings-actions"><span className={aiStatus.keyConfigured ? 'ai-configured' : 'ai-unconfigured'}><span />{aiStatus.keyConfigured ? 'API key configured' : 'API key not configured'}</span><button className="btn btn-secondary" onClick={() => void testAiSettings()} disabled={aiTesting}><Zap size={15} />{aiTesting ? 'Testing...' : 'Test connection'}</button><button className="btn btn-primary" onClick={() => void saveAiSettings()} disabled={aiSaving}><Save size={15} />{aiSaving ? 'Saving...' : 'Save settings'}</button></div>
        </section>
        <section className="card settings-card">
          <div className="settings-title"><Server size={19} /><div><h3>Service status</h3><p>Current connection to the admin API.</p></div></div>
          <div className="service-status">{health?.status === 'healthy' ? <CheckCircle size={20} /> : <span className="service-dot" />}<div><strong>{health?.status === 'healthy' ? 'API is healthy' : 'Status unavailable'}</strong><small>{health?.timestamp ? `Checked ${new Date(health.timestamp).toLocaleString()}` : 'No successful check yet'}</small></div><button className="btn btn-secondary" onClick={() => void checkHealth()} title="Refresh service status"><RefreshCw size={15} /></button></div>
          <dl className="settings-details"><div><dt>API endpoint</dt><dd>{import.meta.env.VITE_API_URL || 'http://localhost:3001'}</dd></div><div><dt>Environment</dt><dd>{import.meta.env.MODE}</dd></div></dl>
        </section>
      </div>
    </div>
  );
}
